import { useTranslation } from "react-i18next";
import type { ReactNode } from "react";
import { MapPin, Video, User, Flag } from "lucide-react";
import {
  ProjectActivity,
  ProjectActivityPriority,
  ProjectActivityType,
} from "services/project-activities.service";
import { formatPrettyDate } from "utils/helperFunctions";

const TYPE_LABELS: Record<
  ProjectActivityType,
  { key: string; fallback: string }
> = {
  CALL: { key: "project_activity_call", fallback: "Call" },
  MEETING: { key: "project_activity_meeting", fallback: "Meeting" },
  TASK: { key: "project_activity_task", fallback: "Task" },
  DEADLINE: { key: "project_activity_deadline", fallback: "Deadline" },
  EMAIL: { key: "project_activity_email", fallback: "Email" },
  LUNCH: { key: "project_activity_lunch", fallback: "Lunch" },
};

const PRIORITY_LABELS: Record<
  ProjectActivityPriority,
  { key: string; fallback: string }
> = {
  LOW: { key: "project_activity_priority_low", fallback: "Low" },
  MEDIUM: { key: "project_activity_priority_medium", fallback: "Medium" },
  HIGH: { key: "project_activity_priority_high", fallback: "High" },
};

export function formatActivityWhen(
  startAt: number | null | undefined,
  endAt?: number | null
): string {
  if (!startAt) return "—";
  const left = formatPrettyDate(startAt, "lt-LT", true);
  if (!endAt) return left;
  return `${left} – ${formatPrettyDate(endAt, "lt-LT", true)}`;
}

type Props = {
  activity: ProjectActivity;
  /** Extra trailing bits (e.g. overdue badge text). */
  trailing?: ReactNode;
  /** Show activity type chip next to meta. Default true. */
  showType?: boolean;
  className?: string;
};

/** Shared rich details under an activity title (Focus / History / list). */
export default function ProjectActivityDetails({
  activity,
  trailing,
  showType = true,
  className = "",
}: Props) {
  const { t } = useTranslation();
  const typeMeta = TYPE_LABELS[activity.activityType];
  const priorityMeta = activity.priority
    ? PRIORITY_LABELS[activity.priority]
    : null;

  const typeLabel = typeMeta
    ? t(typeMeta.key, { defaultValue: typeMeta.fallback })
    : activity.activityType;
  const priorityLabel = priorityMeta
    ? t(priorityMeta.key, { defaultValue: priorityMeta.fallback })
    : null;

  return (
    <div className={`mt-1 space-y-1 ${className}`}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-[var(--p-color-text-secondary)]">
        {showType && (
          <span className="rounded bg-[var(--p-color-bg-surface-secondary)] px-1.5 py-0.5 font-semibold text-[var(--p-color-text)]">
            {typeLabel}
          </span>
        )}
        <span>{formatActivityWhen(activity.startAt, activity.endAt)}</span>
        {activity.assignedTo?.name && (
          <span className="inline-flex items-center gap-1">
            <User className="h-3 w-3 shrink-0" />
            {activity.assignedTo.name}
          </span>
        )}
        {priorityLabel && (
          <span className="inline-flex items-center gap-1">
            <Flag className="h-3 w-3 shrink-0" />
            {priorityLabel}
          </span>
        )}
        {trailing}
      </div>

      {activity.location && (
        <div className="flex items-start gap-1.5 text-xs text-[var(--p-color-text-secondary)]">
          <MapPin className="mt-0.5 h-3 w-3 shrink-0" />
          <span className="min-w-0 break-words">{activity.location}</span>
        </div>
      )}

      {activity.videoCallUrl && (
        <div className="flex items-start gap-1.5 text-xs">
          <Video className="mt-0.5 h-3 w-3 shrink-0 text-[var(--p-color-text-secondary)]" />
          <a
            href={activity.videoCallUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="min-w-0 break-all font-medium text-[var(--p-color-text-link)] hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {activity.videoCallUrl}
          </a>
        </div>
      )}

      {activity.note && (
        <p className="whitespace-pre-wrap text-xs text-[var(--p-color-text-secondary)]">
          {activity.note}
        </p>
      )}
    </div>
  );
}
